/** Products — REAL data. This replaces `fixtures.ts`, which was the bug.
 *
 *  Dattatray, looking at the deployed console after `0062` had already deleted
 *  the row: *"Why do I still see Orbit platform as a product? What Nexcell
 *  line 2 in spaces?"*
 *
 *  Both were hardcoded in `fixtures.ts` (lines 111 and 120). The database was
 *  correct the whole time — the console had simply never read it. Every one of
 *  these pages rendered a literal, which is why two days of schema work were
 *  invisible and why the page looked finished when there was no backend at all.
 *
 *  THE HOOK NAMES ARE DELIBERATELY UNCHANGED from the fixture module, so the
 *  pages change as little as possible and the diff shows WHERE THE DATA COMES
 *  FROM rather than a rewrite. What changes is that they now return a query —
 *  real data has a loading state and a failure state, and a fixture has
 *  neither, which is exactly the difference the pages have to start handling.
 */
import { useQuery } from "@tanstack/react-query";

import { products } from "@/api/client";

export type { PermGroupRow, PermMapRow, PermRow, ProductModelRow, ProductRow, RefFileRow, RefSlotRow, SpaceRow, ToolReleaseRow } from "@/api/client";

/** Poll cadence: these are DEFINITIONS, edited by one super admin, not live
 *  telemetry. Refetching them on a timer would burn the poll budget the UI
 *  performance laws reserve for things that actually move (ARCHITECTURE.md).
 *  They refetch when a mutation invalidates them, and not otherwise. */
const STATIC = { staleTime: 60_000, refetchOnWindowFocus: false } as const;

export const qk = {
  products: ["products"] as const,
  spaces: (kind: string) => ["products", kind, "spaces"] as const,
  maps: (kind: string) => ["products", kind, "maps"] as const,
  models: (kind: string) => ["products", kind, "models"] as const,
  companies: (kind: string) => ["products", kind, "companies"] as const,
  refFiles: (kind: string, modelId?: string, archived?: boolean) =>
    ["products", kind, "ref-files", modelId ?? "_common", !!archived] as const,
  releases: (kind: string) => ["products", kind, "releases"] as const,
};

export function useProducts() {
  return useQuery({ queryKey: qk.products, queryFn: products.list, ...STATIC });
}

export function useSpaces(kind: string) {
  return useQuery({
    queryKey: qk.spaces(kind), queryFn: () => products.spaces(kind),
    enabled: !!kind, ...STATIC,
  });
}

export function usePermissionMaps(kind: string) {
  return useQuery({
    queryKey: qk.maps(kind), queryFn: () => products.maps(kind),
    enabled: !!kind, ...STATIC,
  });
}

export function useProductCompanies(kind: string) {
  return useQuery({
    queryKey: qk.companies(kind), queryFn: () => products.companies(kind),
    enabled: !!kind, ...STATIC,
  });
}

export function useModels(kind: string) {
  return useQuery({
    queryKey: qk.models(kind), queryFn: () => products.models(kind),
    enabled: !!kind, ...STATIC,
  });
}

export function useProductRefFiles(kind: string, modelId?: string,
                                   includeArchived = false) {
  return useQuery({
    queryKey: qk.refFiles(kind, modelId, includeArchived),
    queryFn: () => products.refFiles(kind, modelId, includeArchived),
    enabled: !!kind, ...STATIC,
  });
}

export function useToolReleases(kind: string) {
  return useQuery({
    queryKey: qk.releases(kind), queryFn: () => products.releases(kind),
    enabled: !!kind, ...STATIC,
  });
}

/** The three reference slots. Fixed, and the strings are what the TOOLS send —
 *  renaming one here would stop a bench finding its DID map, so they are
 *  duplicated from the server's `REF_TYPES` rather than invented. */
export const REF_TYPE_LABEL: Record<string, string> = {
  app_config_did: "Application config DIDs",
  mfg_config_did: "Manufacturing config DIDs",
  data_upload_signals: "Data upload signals",
};

export const REF_TYPE_DESC: Record<string, string> = {
  app_config_did: "What the application may read and write on the ECU.",
  mfg_config_did: "What the production bench may set — the manufacturing pass.",
  data_upload_signals: "The signal set a unit reports, and how to decode it.",
};
